/**
 * @module pages/Order
 * @description Order detail page displaying order summary, items, and download packages.
 * Supports both legacy single download links and new multi-package downloads with status polling.
 */

import cx from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { InvoiceView } from 'src/components/InvoiceView';
import { Link } from 'src/components/Link';
import Loading from 'src/components/Loading';
import { PackageView } from 'src/components/PackageView';
import { SanityBadge } from 'src/components/SanityBadge';
import { getNiceDate } from 'src/modules/niceDate';
import { formatUsd } from 'src/modules/utils';
import { useOrders } from 'src/providers/OrdersProvider';
import { Assets } from 'src/types/assets';
import { Invoices } from 'src/types/invoices';
import { Sanity } from 'src/types/sanity';
import * as Styles from './styles';
import { OrderContainerProps, OrderProps } from './types';

/**
 * @func renderItem
 * @summary create markup view for given item in order
 * @param {Sanity.Types.FrozenItem} param0
 * @returns {ReactNode} markup for item
 */
const renderItem = ({
  _key,
  fileTitle,
  category,
  price,
  filmTitle,
  filmSlug,
  thumbnail,
}: Sanity.Types.FrozenItem) => (
  <tr key={_key} className="py05">
    <td
      className={cx('rel pr1 py075', {
        'asset__thumb-square': thumbnail,
      })}
    >
      <div className="rel x y">
        {thumbnail && (
          <img
            className="x y abs fill contain"
            src={thumbnail}
            alt={`thumbnail for ${fileTitle}`}
          />
        )}
      </div>
    </td>

    <td className="p1 m1">
      <h3 className="my025">{fileTitle}</h3>
    </td>

    <td className="order-cell">{formatUsd(price!)}</td>

    <td className="order-cell">
      <div className="mb025">{category}</div>
      Film:{' '}
      <Link to={`/films/${filmSlug}`} className="film__link s16">
        {filmTitle}
      </Link>
    </td>
  </tr>
);

/**
 * @component Order
 * @type {React.FC<Sanity.Types.FrozenOrder>}
 * @summary React Component for order summary, incl. item preview + download link
 * @param {Sanity.Types.FrozenOrder} props
 * @returns {ReactNode} markup for order meta/summary/contents
 */
export const Order = ({
  _createdAt,
  _id: orderId,
  downloadLink,
  invoices,
  items,
  onReplaceOrder,
  orderNumber,
  packages,
  total,
}: OrderProps) => {
  const date = getNiceDate(_createdAt);
  const [newDownloadLinkRequested] = useState(false);

  /**
   * @func areAllInvoicesPaid
   * @summary Check if all invoices for this order are fully paid
   * @returns {boolean} True if all invoices are paid, false otherwise
   */
  const areAllInvoicesPaid = useMemo(() => {
    if (!invoices || invoices.length === 0) {
      // No invoices means legacy order - allow package downloads
      return true;
    }
    return invoices.every((invoice) =>
      invoice.paid === true || invoice.status === Invoices.Types.InvoiceStatus.PAID
    );
  }, [invoices]);

  const invoiceBlocks: React.ReactNode[] = [];
  const packageBlocks: React.ReactNode[] = [];

  const hasInvoices = Array.isArray(invoices) && invoices.length > 0;
  const hasPackages = Array.isArray(packages) && packages.length > 0;

  // Render invoice views
  if (hasInvoices) {
    invoices.forEach((invoice, i) => {
      invoiceBlocks.push(
        <InvoiceView
          key={`invoice-view-${invoice.id}`}
          invoice={invoice}
          orderId={orderId}
          index={i}
          totalCount={invoices.length}
          onReplaceOrder={onReplaceOrder}
        />,
      );
    });
  }

  // Only render package views if all invoices are paid
  if (areAllInvoicesPaid) {
    if (hasPackages) {
      packages.forEach((_package, i) => {
        packageBlocks.push(
          <PackageView
            key={`package-view-${_package._key}`}
            assetPackage={_package}
            orderId={orderId}
            index={i}
            totalCount={packages.length}
            onReplaceOrder={onReplaceOrder}
          />,
        );
      });
    } else if (downloadLink) {
      packageBlocks.push(
        <PackageView
          key="legacy-download"
          orderId={orderId}
          downloadLink={downloadLink}
          newDownloadLinkRequested={newDownloadLinkRequested}
        />,
      );
    }
  }

  return (
    <div className="container--s mxa mt1">
      <div className="f jcb aie" style={{ alignItems: 'center' }}>
        <div>
          <Styles.OrderHeader>
            Order <span className="h2">#</span>
            {orderNumber}
          </Styles.OrderHeader>
          <div className="s18">{date.long}</div>
        </div>
        <div className="ar a22">
          <Styles.OrderTotal>
            {formatUsd(total)}
          </Styles.OrderTotal>
        </div>
      </div>

      { invoiceBlocks.length > 0 && (
        <Styles.InvoiceList>
          {invoiceBlocks.map((block, index) => (
            <Styles.OrderPackageItem key={index}>{block}</Styles.OrderPackageItem>
          ))}
        </Styles.InvoiceList>
      )}

      { 1 && (
        <>
          <hr className="p mt05 mb075" />

          <div>
            <Styles.OrderPackageHeader>Packages</Styles.OrderPackageHeader>
            { !areAllInvoicesPaid && hasInvoices && (

                <Styles.OrderPackageDenied>
                  <SanityBadge
                    tone={Sanity.Types.SanityTone.CRITICAL}
                    outline
                    // icon={<RiAlertFill />}
                    icon={Assets.Data.ExpiryStatusIcons[Assets.Types.ExpiryStatus.CRITICAL]}
                  >
                    Access Denied
                  </SanityBadge>
                  <span>Please pay the invoice(s) above to access your packages</span>
                </Styles.OrderPackageDenied>
            )}
            { areAllInvoicesPaid && packageBlocks.length > 0 && (
              <Styles.OrderPackageList>
                {packageBlocks.map((block, index) => (
                  <Styles.OrderPackageItem key={index}>{block}</Styles.OrderPackageItem>
                ))}
              </Styles.OrderPackageList>
            )}
          </div>
        </>
      )}

      <hr className="p mt05 mb075" />

      <table className="mxa x">
        <tbody>{items.map(renderItem)}</tbody>
      </table>
    </div>
  );
};

/**
 * @typedef OrderContainerProps
 * @prop {string|number} orderId id for order (**not order number**). will be used to retrieve order data
 */

/**
 * @component OrderContainer
 * @summary React Component for wrapper for orders listing
 * @param {React.FC<OrderContainerProps>} props component props
 * @returns {ReactNode} markup for complete order listing (wraps `Order` above)
 */
export const OrderContainer = ({
  orderId,
  className,
  ...props
}: OrderContainerProps) => {
  // @ts-ignore
  const { getOrder } = useOrders();
  const [order, setOrder] = useState<Sanity.Types.FrozenOrder | null>(null);

  useEffect(() => {
    getOrder(orderId).then(setOrder);
  }, [getOrder, orderId]);

  return (
    <div className={cx('orders', className)}>
      <div className={'x film mb1'}>
        <Link to="/orders" className="film__back if jcs aic">
          <svg
            fill="currentColor"
            className="ml1 rotate"
            version="1.1"
            id="Layer_1"
            x="0px"
            y="0px"
            viewBox="0 0 55 20"
          >
            <polygon
              fillRule="evenodd"
              clipRule="evenodd"
              points="43.3,0 55,10 43.3,20 42.4,19.1 52.2,10.7 0,10.7 0,9.3 52.2,9.3   42.4,0.9 43.3,0 "
            />
          </svg>
          <h5 className="p0 my0 pl1 bcb rel z1">Back</h5>
        </Link>
      </div>

      <Loading ready={!!order}>{order && <Order {...order} onReplaceOrder={setOrder} />}</Loading>
    </div>
  );
};

export default OrderContainer;
